import { useState, useRef } from "react"
import { useNavigate } from "react-router"
import {
    Box, Button, ButtonGroup, Flex, Grid, Input,
    NumberInput, NumberInputField, Select, Spinner, Text,
    useColorModeValue, useToast
} from "@chakra-ui/react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { Breadcrumb, Container, Heading, Icon, IconButton } from "components"
import { API, Mask } from "config"
import { useAllDistributors } from "hooks"

// ─── Helpers ──────────────────────────────────────────────────────────────────

const FieldLabel = ({ children, required, hint }) => (
    <Box mb={1.5}>
        <Text fontSize="sm" fontWeight="600" color="gray.700" _dark={{ color: 'gray.200' }}>
            {children}{required && <Box as="span" color="red.400" ml={0.5}>*</Box>}
        </Text>
        {hint && <Text fontSize="xs" color="gray.400" _dark={{ color: 'gray.500' }} mt={0.5}>{hint}</Text>}
    </Box>
)

const FileUploadZone = ({ file, onFile, hint }) => {
    const inputRef = useRef()
    const bg       = useColorModeValue('gray.50', 'gray.750')
    const border   = useColorModeValue('gray.300', 'gray.600')

    if (file) {
        return (
            <Flex align="center" gap={3}
                border="1.5px solid" borderColor="teal.300" bg="teal.50"
                _dark={{ bg: 'teal.900', borderColor: 'teal.500' }}
                borderRadius="lg" px={4} py={3}
            >
                <Icon name="check" boxSize={5} color="teal.500" />
                <Box flex={1} minW={0}>
                    <Text fontSize="sm" fontWeight="semibold" color="teal.700"
                        _dark={{ color: 'teal.200' }} noOfLines={1}>{file.name}</Text>
                    <Text fontSize="xs" color="teal.500">{(file.size / 1024).toFixed(0)} KB</Text>
                </Box>
                <Button size="xs" variant="ghost" colorScheme="gray"
                    onClick={() => { onFile(null); if (inputRef.current) inputRef.current.value = '' }}>
                    Trocar
                </Button>
            </Flex>
        )
    }

    return (
        <>
            <Flex
                as="button" type="button" direction="column" align="center" justify="center"
                gap={2} w="100%" minH="72px" borderRadius="lg"
                border="2px dashed" borderColor={border}
                bg={bg} _hover={{ borderColor: 'teal.400', bg: 'teal.50' }}
                transition="all 0.2s" cursor="pointer" px={4}
                onClick={() => inputRef.current?.click()}
            >
                <Icon name="upload" boxSize={5} color="gray.400" />
                <Text fontSize="sm" color="gray.500" _dark={{ color: 'gray.400' }}>
                    Clique para selecionar (opcional)
                </Text>
                {hint && <Text fontSize="xs" color="gray.400">{hint}</Text>}
            </Flex>
            <input ref={inputRef} type="file"
                accept=".xml,.pdf,.jpg,.jpeg,.png,.webp,.mp4,.mov,.avi"
                style={{ display: 'none' }}
                onChange={e => onFile(e.target.files?.[0] ?? null)} />
        </>
    )
}

// ─── ProductSearch ────────────────────────────────────────────────────────────

const ProductSearch = ({ value, label, onSelect }) => {
    const [term, setTerm]               = useState(label || '')
    const [debouncedTerm, setDebounced] = useState('')
    const [open, setOpen]               = useState(false)

    const handleChange = (e) => {
        const v = e.target.value
        setTerm(v)
        setOpen(true)
        clearTimeout(window._provProdTimer)
        window._provProdTimer = setTimeout(() => setDebounced(v), 350)
    }

    const { data: products, isFetching } = useQuery({
        queryKey: ['product-search-prov', debouncedTerm],
        queryFn:  async () => {
            const res = await API.get(`/products?term=${debouncedTerm}`)
            return Array.isArray(res.data) ? res.data : res.data?.data ?? []
        },
        enabled:   debouncedTerm.length >= 2,
        staleTime: 30000,
    })

    const handleSelect = (p) => { onSelect(p); setTerm(p.name); setOpen(false) }
    const handleClear  = () => { onSelect(null); setTerm(''); setDebounced('') }

    if (value) {
        return (
            <Flex align="center" border="1.5px solid" borderColor="teal.300"
                bg="teal.50" _dark={{ bg: 'teal.900', borderColor: 'teal.500' }}
                borderRadius="md" px={3} gap={2} h="40px"
            >
                <Icon name="product" boxSize={4} color="teal.500" />
                <Text fontSize="sm" flex={1} noOfLines={1} fontWeight="semibold"
                    color="teal.700" _dark={{ color: 'teal.200' }}>
                    {label || value}
                </Text>
                <IconButton icon="cancel" size="sm" variant="ghost" colorScheme="red"
                    aria-label="Remover produto" onClick={handleClear} />
            </Flex>
        )
    }

    return (
        <Box position="relative">
            <Input size="md" value={term} placeholder="Buscar produto por nome..."
                onChange={handleChange}
                onFocus={() => setOpen(true)}
                onBlur={() => setTimeout(() => setOpen(false), 180)}
            />
            {open && term.length >= 2 && (
                <Box position="absolute" top="calc(100% + 4px)" left={0} right={0}
                    bg="white" border="1px solid" borderColor="gray.200"
                    _dark={{ bg: 'gray.800', borderColor: 'gray.600' }}
                    borderRadius="md" boxShadow="lg" zIndex={1500} maxH="220px" overflowY="auto"
                >
                    {isFetching && <Flex justify="center" py={4}><Spinner size="sm" /></Flex>}
                    {!isFetching && (!products || products.length === 0) && (
                        <Text fontSize="sm" color="gray.500" px={4} py={3}>
                            Nenhum produto encontrado
                        </Text>
                    )}
                    {(products ?? []).slice(0, 12).map(p => (
                        <Box key={p.id} px={4} py={2.5} cursor="pointer"
                            _hover={{ bg: 'teal.50' }} _dark={{ _hover: { bg: 'gray.700' } }}
                            onMouseDown={() => handleSelect(p)}
                        >
                            <Text fontSize="sm" fontWeight="semibold">{p.name}</Text>
                            {p.cod && <Text fontSize="xs" color="gray.400">Cód: {p.cod}</Text>}
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    )
}

// ─── Página ───────────────────────────────────────────────────────────────────

const EMPTY_ITEM = { product_id: '', product_name: '', qty: 1, unit_cost: '', expires_at: '' }

export default function ProvisionalInvoice() {
    const navigate     = useNavigate()
    const toast        = useToast()
    const queryClient  = useQueryClient()
    const distributors = useAllDistributors()

    const [loading, setLoading] = useState(false)
    const [errors, setErrors]   = useState({})
    const [form, setForm] = useState({
        distributor_id: '',
        supplier_name:  '',
        notes:          '',
    })
    const [items, setItems]       = useState([{ ...EMPTY_ITEM }])
    const [evidenceFile, setFile] = useState(null)

    const warehouses = (distributors.data ?? []).filter(d => d.is_warehouse)

    const set     = (key, val) => { setForm(f => ({ ...f, [key]: val })); setErrors(e => ({ ...e, [key]: null })) }
    const addItem = () => setItems(prev => [...prev, { ...EMPTY_ITEM }])
    const rmItem  = (i) => setItems(prev => prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev)
    const setItem = (i, patch) => setItems(prev => prev.map((it, idx) => idx === i ? { ...it, ...patch } : it))

    const validate = () => {
        const e = {}
        if (!form.distributor_id) e.distributor_id = 'Selecione o núcleo de destino'
        const valid = items.filter(it => it.product_id)
        if (!valid.length) e.items = 'Adicione ao menos um produto'
        setErrors(e)
        return Object.keys(e).length === 0
    }

    const handleSubmit = async () => {
        if (!validate()) return

        const validItems = items.filter(it => it.product_id)
        const fd = new FormData()
        fd.append('distributor_id', form.distributor_id)
        if (form.supplier_name) fd.append('supplier_name', form.supplier_name)
        if (form.notes)         fd.append('notes', form.notes)
        if (evidenceFile)       fd.append('file', evidenceFile)
        validItems.forEach((it, i) => {
            fd.append(`items[${i}][product_id]`, it.product_id)
            fd.append(`items[${i}][qty]`,        Number(it.qty))
            if (it.unit_cost)  fd.append(`items[${i}][unit_cost]`,  parseInt(it.unit_cost.replace(/\D/g, '')) / 100)
            if (it.expires_at) fd.append(`items[${i}][expires_at]`, it.expires_at)
        })

        setLoading(true)
        try {
            const res = await API.post('/invoices/provisional', fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            })
            toast({ title: 'Entrada provisória registrada', status: 'success', duration: 4000 })
            queryClient.invalidateQueries({ queryKey: ['invoices-list'] })
            queryClient.invalidateQueries({ queryKey: ['invoices-provisional'] })
            navigate(`/stock/invoices/${res.data.id}`)
        } catch (err) {
            const msg = err?.response?.data?.message ?? 'Erro ao registrar entrada provisória'
            toast({ title: msg, status: 'error', duration: 6000 })
        } finally {
            setLoading(false)
        }
    }

    const cardBg     = useColorModeValue('white', 'gray.800')
    const cardBorder = useColorModeValue('gray.200', 'gray.700')
    const labelColor = useColorModeValue('gray.500', 'gray.400')

    return (
        <Container maxW="90%" mt={10} pb={20}>
            <Breadcrumb items={[
                { label: 'NF-e / Entradas', to: '/stock/invoices' },
                { label: 'Entrada sem Nota' },
            ]} mb={4} />

            <Heading
                title="Entrada sem Nota Fiscal"
                description="Registre o recebimento de produtos antes da NF-e chegar. O lote será gerado automaticamente e conciliado depois."
            >
                <ButtonGroup size="sm">
                    <Button variant="ghost" onClick={() => navigate('/stock/invoices')} isDisabled={loading}>
                        Cancelar
                    </Button>
                    <Button colorScheme="purple" isLoading={loading} onClick={handleSubmit}
                        leftIcon={<Icon name="check" />} loadingText="Registrando...">
                        Registrar Entrada
                    </Button>
                </ButtonGroup>
            </Heading>

            {/* Banner informativo */}
            <Flex align="flex-start" gap={3} mt={4}
                bg="purple.50" border="1px solid" borderColor="purple.200"
                _dark={{ bg: 'purple.900', borderColor: 'purple.600' }}
                borderRadius="lg" px={5} py={4}
            >
                <Box mt={0.5} flexShrink={0}>
                    <Icon name="stock" boxSize={5} color="purple.500" />
                </Box>
                <Box>
                    <Text fontSize="sm" fontWeight="bold" color="purple.700" _dark={{ color: 'purple.200' }}>
                        Entrada provisória — estoque creditado imediatamente
                    </Text>
                    <Text fontSize="xs" color="purple.600" _dark={{ color: 'purple.300' }} mt={1}>
                        Um lote provisório será criado automaticamente para rastrear cada item.
                        Quando a NF-e chegar, acesse <Box as="span" fontWeight="bold">Conciliações</Box> para
                        vincular os números de lote reais e o documento fiscal.
                    </Text>
                </Box>
            </Flex>

            <Grid templateColumns={{ base: '1fr', xl: '380px 1fr' }} gap={6} mt={6} alignItems="start">

                {/* Coluna esquerda: cabeçalho */}
                <Box>
                    <Box
                        bg={cardBg} border="1px solid" borderColor={cardBorder}
                        borderRadius="2xl"
                        boxShadow="0 4px 6px -1px rgba(0,0,0,0.07)"
                    >
                        <Flex align="center" gap={3} px={6} py={4}
                            bg="gray.800" _dark={{ bg: 'gray.900' }}
                            borderTopRadius="2xl"
                        >
                            <Flex align="center" justify="center"
                                w={10} h={10} borderRadius="xl" flexShrink={0}
                                bgGradient="linear(to-br, purple.400, purple.600)"
                                boxShadow="0 2px 8px rgba(0,0,0,0.25)"
                            >
                                <Icon name="stock" boxSize={4} color="white" />
                            </Flex>
                            <Box>
                                <Text fontSize="sm" fontWeight="800" color="white">Dados da Entrada</Text>
                                <Text fontSize="xs" color="gray.400">Núcleo destino e informações</Text>
                            </Box>
                        </Flex>

                        <Flex direction="column" gap={5} px={6} py={6}>
                            <Box>
                                <FieldLabel required>Núcleo de Destino</FieldLabel>
                                {distributors.isLoading ? (
                                    <Flex align="center" gap={2} h="40px">
                                        <Spinner size="sm" />
                                        <Text fontSize="sm" color={labelColor}>Carregando...</Text>
                                    </Flex>
                                ) : (
                                    <Select size="md" value={form.distributor_id}
                                        onChange={e => set('distributor_id', e.target.value)}
                                        isInvalid={!!errors.distributor_id}
                                        placeholder="Selecione o núcleo de destino">
                                        {warehouses.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                                    </Select>
                                )}
                                {errors.distributor_id && (
                                    <Text fontSize="xs" color="red.500" mt={1}>{errors.distributor_id}</Text>
                                )}
                            </Box>

                            <Box>
                                <FieldLabel hint="Será informado na conciliação se não souber agora">
                                    Fornecedor (opcional)
                                </FieldLabel>
                                <Input size="md" value={form.supplier_name}
                                    placeholder="Nome do fornecedor..."
                                    onChange={e => set('supplier_name', e.target.value)} />
                            </Box>

                            <Box>
                                <FieldLabel hint="Foto, vídeo ou PDF do recebimento — opcional">
                                    Evidência de Recebimento
                                </FieldLabel>
                                <FileUploadZone
                                    file={evidenceFile}
                                    onFile={setFile}
                                    hint="JPG, PNG, PDF, MP4, XML..."
                                />
                            </Box>

                            <Box>
                                <FieldLabel>Observações</FieldLabel>
                                <Input size="md" as="textarea" rows={3}
                                    value={form.notes} placeholder="Informações adicionais sobre esta entrada..."
                                    onChange={e => set('notes', e.target.value)}
                                    style={{ resize: 'vertical', minHeight: '72px', paddingTop: '8px' }}
                                />
                            </Box>
                        </Flex>
                    </Box>
                </Box>

                {/* Coluna direita: itens */}
                <Box>
                    <Box
                        bg={cardBg} border="1px solid" borderColor={cardBorder}
                        borderRadius="2xl"
                        boxShadow="0 4px 6px -1px rgba(0,0,0,0.07)"
                    >
                        <Flex align="center" gap={3} px={6} py={4}
                            bg="gray.800" _dark={{ bg: 'gray.900' }}
                            borderTopRadius="2xl"
                        >
                            <Flex align="center" justify="center"
                                w={10} h={10} borderRadius="xl" flexShrink={0}
                                bgGradient="linear(to-br, teal.400, teal.600)"
                                boxShadow="0 2px 8px rgba(0,0,0,0.25)"
                            >
                                <Icon name="product" boxSize={4} color="white" />
                            </Flex>
                            <Box flex={1}>
                                <Text fontSize="sm" fontWeight="800" color="white">Produtos Recebidos</Text>
                                <Text fontSize="xs" color="gray.400">
                                    {items.filter(it => it.product_id).length} de {items.length} vinculado(s)
                                </Text>
                            </Box>
                            <Button size="sm" colorScheme="teal" variant="solid"
                                leftIcon={<Icon name="new" boxSize={3.5} />}
                                onClick={addItem}
                                borderRadius="full"
                            >
                                Adicionar item
                            </Button>
                        </Flex>

                        <Box px={6} py={6}>
                            {errors.items && (
                                <Text fontSize="sm" color="red.500" mb={4}>{errors.items}</Text>
                            )}

                            <Flex direction="column" gap={4}>
                                {items.map((item, i) => (
                                    <Box key={i}
                                        border="1.5px solid"
                                        borderColor={item.product_id ? 'teal.400' : 'gray.200'}
                                        _dark={{ borderColor: item.product_id ? 'teal.600' : 'gray.600' }}
                                        borderRadius="xl" transition="border-color 0.2s"
                                    >
                                        {/* Header do item */}
                                        <Flex align="center" gap={3} px={5} py={3}
                                            borderTopRadius="xl"
                                            bgGradient={item.product_id
                                                ? 'linear(to-r, teal.700, teal.800)'
                                                : 'linear(to-r, gray.600, gray.700)'
                                            }
                                        >
                                            <Flex align="center" justify="center"
                                                w={7} h={7} borderRadius="full" flexShrink={0}
                                                bg="white" fontSize="xs" fontWeight="900"
                                                color={item.product_id ? 'teal.600' : 'gray.600'}
                                            >
                                                {i + 1}
                                            </Flex>
                                            <Text fontSize="sm" fontWeight="700" color="white" flex={1} noOfLines={1}>
                                                {item.product_name || `Item ${i + 1}`}
                                            </Text>
                                            {items.length > 1 && (
                                                <Box as="button" onClick={() => rmItem(i)} lineHeight={0} p={1.5}
                                                    borderRadius="md" color="whiteAlpha.600"
                                                    _hover={{ color: 'red.300', bg: 'blackAlpha.300' }}
                                                    transition="all 0.15s"
                                                >
                                                    <Icon name="cancel" boxSize={4} />
                                                </Box>
                                            )}
                                        </Flex>

                                        <Box p={5} bg={cardBg}>
                                            <Grid templateColumns={{ base: '1fr', md: '1fr 100px 130px 165px' }} gap={4} alignItems="flex-end">
                                                <Box>
                                                    <FieldLabel required>Produto</FieldLabel>
                                                    <ProductSearch
                                                        value={item.product_id}
                                                        label={item.product_name}
                                                        onSelect={p => setItem(i, {
                                                            product_id:   p?.id   ?? '',
                                                            product_name: p?.name ?? '',
                                                        })}
                                                    />
                                                </Box>
                                                <Box>
                                                    <FieldLabel required>Qtd.</FieldLabel>
                                                    <NumberInput size="md" min={1} value={item.qty}
                                                        onChange={v => setItem(i, { qty: v })}>
                                                        <NumberInputField placeholder="1" />
                                                    </NumberInput>
                                                </Box>
                                                <Box>
                                                    <FieldLabel>Custo un. (R$)</FieldLabel>
                                                    <Input size="md" placeholder="0,00"
                                                        value={item.unit_cost}
                                                        onChange={e => setItem(i, { unit_cost: e.target.value ? Mask.price(e.target.value) : '' })} />
                                                </Box>
                                                <Box>
                                                    <FieldLabel hint="Preenchida na conciliação se souber">
                                                        Validade (opcional)
                                                    </FieldLabel>
                                                    <Input size="md" type="date" value={item.expires_at}
                                                        onChange={e => setItem(i, { expires_at: e.target.value })} />
                                                </Box>
                                            </Grid>

                                            {item.product_id && (
                                                <Flex align="center" gap={2} mt={4}
                                                    bg="purple.50" _dark={{ bg: 'rgba(128,90,213,0.12)' }}
                                                    borderRadius="lg" px={4} py={3}
                                                >
                                                    <Icon name="check" boxSize={4} color="purple.400" />
                                                    <Text fontSize="xs" color="purple.700" _dark={{ color: 'purple.300' }}>
                                                        Lote provisório gerado automaticamente pelo sistema
                                                        {item.expires_at ? ' · Validade pré-configurada' : ' · Validade a definir na conciliação'}
                                                    </Text>
                                                </Flex>
                                            )}
                                        </Box>
                                    </Box>
                                ))}
                            </Flex>
                        </Box>
                    </Box>
                </Box>
            </Grid>
        </Container>
    )
}
